import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';
import './App.css';
import { IData } from './interfaces';
import Events from './components/Events';
import Cubes from './components/Cubes';
import { orange, purple } from '@mui/material/colors';
import { Box, Card, CardMedia, CircularProgress, CssBaseline, IconButton, Link, Modal, ThemeProvider, Typography, createTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Vertex from './components/Vertex';

function App() {
  const [data, setData]: [IData, any] = useState({events: [], cubes: []});
  const [resolved, setResolved]: [boolean, any] = useState(false)
  const [error, setError]: [boolean, any] = useState(false)
  const [open, setOpen]: [boolean, any] = useState(false);
  const [loadedImages, setLoadedImages]: [string[], any] = useState([]);

  const preloadImage = (src: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        resolve(img);
        setLoadedImages([...loadedImages, src])
      };
      img.onerror = reject;
    });
  }

  useEffect(() => {
    const url = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : `${window.location.protocol}//${window.location.hostname}/api/`
    // USED IN PROD
    fetch(`${url}`)
      .then((res) => res.json())
      .then(setData)
      .then(() => setResolved(true))
      .catch(() => setError(true))
    // END PROD
    // USED IN TEST
    // setData({"cubes":[{"name":"pulp nouveau","owner":"parker .lam","url":"https://cubecobra.com/cube/list/pulpnouveau","image":{"artist":"Anato Finnstark","url":"https://cards.scryfall.io/art_crop/front/7/2/72a05539-ec37-46b4-92f3-feda5f02ddb3.jpg?1664503151"}},{"description":"a lot of goblins, a lot of old jank, and a few recent, quirky cards that pair well with janky goblins!", "url": "https://cubecobra.com/cube/overview/spicegoblins","name":"goblins","owner":"spicelike"},{"name":"theseus","owner":"parker .lam","url":"https://cubecobra.com/cube/list/131313"},{"name":"chesecube","owner":"chesement","url":"https://cubecobra.com/cube/overview/cb1d0cc0-a11c-4cd2-a97b-3b906d7938af"},{"name":"Standard Cube","owner":"VedalkenSamurai (Dan)","url":"https://www.cubecobra.com/cube/overview/3lyrh"},{"name":"Eldrazi Cube","owner":"VedalkenSamurai (Dan)","url":"https://www.cubecobra.com/cube/overview/10be"},{"name":"Jason 180","owner":"jms/Jason","url":"https://cubecobra.com/cube/list/jasoncubejr"},{"name":"Jason 360","owner":"jms/Jason","url":"https://cubecobra.com/cube/list/jasoncube"}],"events":[{"cube":"Everyone Lives Twice","date":"Wed, 26 Jun 2024 18:30:00 GMT","description":"Recursion is the name of the game","location":"Redcap's Corner","owner":"rosia","players":8.0},{"cube":"Pulp Nouveau","date":"Wed, 03 Jul 2024 18:30:00 GMT","description":"GOONIES","location":"Philly Game Shop","owner":"Parker L - @landofmordor","players":8.0},{"cube":"Standard Cube","date":"Wed, 10 Jul 2024 18:30:00 GMT","description":"All the best cards in Standard (currently through Outlaws of Thunder Junction)","hyperlink":"https://www.cubecobra.com/cube/overview/3lyrh","location":"Redcap's Corner","owner":"Dan - @VedalkenSamurai","players":8.0},{"cube":"Some Cube","date":"Wed, 17 Jul 2024 18:30:00 GMT","location":"Philly Game Shop","owner":"Jack - @Jack","players":10.0},{"cube":"Craig's Cube!","date":"Wed, 24 Jul 2024 18:30:00 GMT","description":"High power pretty cards","hyperlink":"https://cubecobra.com/cube/list/craig","location":"Redcap's Corner","owner":"Craig @crustycraig","players":12.0}]})
    // setResolved(true)
    // END TEST
    preloadImage('leapinglizards.png')
    preloadImage('vertex.png')
    preloadImage('https://cards.scryfall.io/art_crop/front/7/9/79e5bc79-2a98-4389-9424-be01f8beeb81.jpg?1562767053')
  }, []);

  useEffect(() => {
    data.cubes.forEach((cube) => {
      if (cube.image?.url && !loadedImages.includes(cube.image.url)) preloadImage(cube.image.url);
    })
  }, [data])

  const theme = createTheme({
    palette: {
      mode: 'dark',
      primary: orange,
      secondary: purple,
    },
  })

  const router = createBrowserRouter(([
    {
      path: '/',
      element: <Events events={data.events} resolved={resolved}  />
    },
    {
      path: '/cubes',
      element: <Cubes cubes={data.cubes} />
    },
    {
      path: '/vertex',
      element: <Vertex />
    },
    {
      path: "*",
      element: <Navigate to="/" replace />
    }
  ]))
   
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className='App'>
      <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
          <div>
            <p><b>Schuylkill Cube Social</b> is a Magic: the Gathering playgroup in Philadelphia, PA.</p>
            <p>We play <Link target="_blank" rel="noopener noreferrer" href='https://luckypaper.co/what-is-a-cube/'>Cube Draft<OpenInNewIcon /></Link> every week in Center City or West Philly. Everyone is welcome! Join us at any event below, or hop in our <Link target="_blank" rel="noopener noreferrer" color='secondary' href='https://discord.gg/YSMWyywsrP'>Discord<OpenInNewIcon /></Link> to hang out.</p>
          </div>
          <IconButton onClick={() => setOpen(true)} aria-label="lizard" sx={{aspectRatio: "1 / 1", marginTop: "auto", marginBottom: "auto"}}>🦎</IconButton>
        </Box>
        <RouterProvider router={router} />
        {!resolved && !error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}} className='spinner'>
            <CircularProgress color="primary" />
            <Typography>Shuffling up...</Typography>
          </Box>
        ) : <></>}
        {error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}} className='spinner'>
            <Typography>We had some trouble loading everything.</Typography>
            <Typography>Please try again in a few.</Typography>
            <div className="cardArt" style={{marginTop: '1rem'}}>
              <img style={{borderRadius: '4px'}} src="https://cards.scryfall.io/art_crop/front/7/9/79e5bc79-2a98-4389-9424-be01f8beeb81.jpg?1562767053"></img>
              <em>{`Art by Jeff Miracola`}</em>
            </div>
          </Box>
        ) : <></>}
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          sx={{aspectRatio: '315 / 461', margin: 'auto', maxHeight: '80vh', maxWidth: '80vw'}}
        >
          <Card sx={{display: 'block'}}>
            <IconButton onClick={() => setOpen(false)} aria-label="close" sx={{position: 'absolute', top: 0, right: 0}}><CloseIcon /></IconButton>
            <CardMedia
              sx={{height: '100%', margin: 'auto'}}
              image="leapinglizards.png"
              title="Leaping Lizards">
            </CardMedia>
          </Card>
        </Modal>
      </div>
    </ThemeProvider>
  );
}

export default App;
